import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useQuery, useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  GET_PUBLIC_LOCATIONS,
  CREATE_PUBLIC_LOCATION,
  UPDATE_LOCATION,
  SET_BRAND_AIRPORT_ENABLED,
  SET_AIRPORT_ACTIVE,
} from '../../services/locationServices/queries';
import { useSnapshot } from 'valtio';
import { authStore } from '../../../store/authStore';
import { LocationType } from '../../../__generated__/graphql';
import { streetAddressForLocationSave } from '../../helpers/formatAddress';
import { useBrandRules } from '../../hooks/useHostCapabilities';

import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import SwitchComponent from '../../components/switch';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  EmptyState,
  Field,
  FormGrid,
  InlineRow,
  Input,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
  Strong,
  Table,
  TableWrap,
  TBody,
  TD,
  TDActions,
  Textarea,
  TH,
  THead,
  TR,
} from '../../components/ui';

const ModalTitle = styled.h3`
  ${tw`text-lg font-semibold mb-4`}
  color: var(--color-text-primary);
  font-family: var(--font-display);
`;

const ModalActions = styled.div`
  ${tw`flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-6`}
`;

const SettingsLink = styled(Link)`
  ${tw`font-medium underline`}
  color: inherit;
`;

const ToggleLabel = styled.label`
  ${tw`inline-flex items-center gap-2 cursor-pointer text-sm`}
  color: var(--color-text-secondary);
`;

type PublicLocation = {
  id: number;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  latitude: number;
  longitude: number;
  taxRate?: number;
  phoneNumber?: string;
  description?: string;
  airportCode?: string | null;
  isActive?: boolean;
  enabledOnBrand?: boolean | null;
};

const AdminLocationsPage = () => {
  const { user } = useSnapshot(authStore);
  const isPlatformOwner = Boolean((user as any)?.isPlatformOwner);
  // D38: the list shows every airport, including ones another brand created or
  // ones that are off, so an existing airport is turned on, not created again.
  const { data, loading, refetch } = useQuery(GET_PUBLIC_LOCATIONS, {
    variables: { type: LocationType.Airport, allAirports: true },
  });
  const [createPublicLocation, { loading: creating }] = useMutation(CREATE_PUBLIC_LOCATION);
  const [setBrandAirportEnabled, { loading: togglingBrand }] =
    useMutation(SET_BRAND_AIRPORT_ENABLED);
  const [setAirportActive, { loading: togglingActive }] = useMutation(SET_AIRPORT_ACTIVE);
  const [updateLocation, { loading: updating }] = useMutation(UPDATE_LOCATION);
  // Spec 014c FR-013: with delivery off the switches below stay usable and apply later.
  const { deliveryOffered } = useBrandRules();
  const autocompleteInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const editAutocompleteInputRef = useRef<HTMLInputElement>(null);
  const editAutocompleteRef = useRef<any>(null);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [editingLocation, setEditingLocation] = useState<PublicLocation | null>(null);

  const [formData, setFormData] = useState({
    airportCode: '',
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    latitude: '',
    longitude: '',
    taxRate: '',
    phoneNumber: '',
    description: '',
  });

  const [editFormData, setEditFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    latitude: '',
    longitude: '',
    taxRate: '',
    phoneNumber: '',
    description: '',
  });

  const handleChange = (key: string, value: string) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleEditChange = (key: string, value: string) => {
    setEditFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleEdit = (location: PublicLocation) => {
    setEditingLocation(location);
    setEditFormData({
      name: location.name || '',
      address: location.address || '',
      city: location.city || '',
      state: location.state || '',
      zipCode: location.zipCode || '',
      latitude: String(location.latitude || ''),
      longitude: String(location.longitude || ''),
      taxRate: location.taxRate ? String(location.taxRate) : '',
      phoneNumber: location.phoneNumber || '',
      description: location.description || '',
    });
  };

  const handleCloseEdit = () => {
    setEditingLocation(null);
    setEditFormData({
      name: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      latitude: '',
      longitude: '',
      taxRate: '',
      phoneNumber: '',
      description: '',
    });
  };

  const handleEditPlaceSelection = (place: any) => {
    if (!place?.geometry?.location) {
      toast.error('No details available for the selected place');
      return;
    }

    let cityName = '';
    let sublocalityName = '';
    let postalTownName = '';
    let adminLevel2Name = '';
    let stateName = '';
    let zipCodeValue = '';
    let country = '';

    place.address_components?.forEach((component: any) => {
      const types = component.types;
      if (types.includes('locality')) {
        cityName = component.long_name;
      }
      if (types.includes('postal_town')) {
        postalTownName = component.long_name;
      }
      if (types.includes('sublocality_level_1') || types.includes('sublocality')) {
        sublocalityName = component.long_name;
      }
      if (types.includes('administrative_area_level_2')) {
        adminLevel2Name = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        stateName = component.short_name;
      }
      if (types.includes('postal_code')) {
        zipCodeValue = component.long_name;
      }
      if (types.includes('country')) {
        country = component.short_name;
      }
    });

    const fullAddress = place.formatted_address || '';
    let resolvedCity = cityName || sublocalityName || postalTownName || adminLevel2Name;
    if (fullAddress) {
      const addressParts = fullAddress.split(',').map((part: string) => part.trim());
      const addressCity = addressParts.length >= 2 ? addressParts[1] : '';
      if (addressCity && addressCity !== stateName && addressCity !== country) {
        resolvedCity = addressCity;
      }
    }

    const location = place.geometry.location;
    const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
    const lng = typeof location.lng === 'function' ? location.lng() : location.lng;

    setEditFormData(prev => ({
      ...prev,
      name: prev.name || place.name || '',
      address: fullAddress,
      city: resolvedCity,
      state: stateName,
      zipCode: zipCodeValue,
      latitude: String(lat),
      longitude: String(lng),
    }));
  };

  const handleSaveEdit = async () => {
    if (!editingLocation) return;

    try {
      const persistedAddress = streetAddressForLocationSave(
        editFormData.address,
        editFormData.city,
        editFormData.state,
        editFormData.zipCode,
      );
      await updateLocation({
        variables: {
          id: editingLocation.id,
          input: {
            name: editFormData.name,
            address: persistedAddress,
            city: editFormData.city,
            state: editFormData.state,
            zipCode: editFormData.zipCode,
            latitude: Number(editFormData.latitude),
            longitude: Number(editFormData.longitude),
            taxRate: editFormData.taxRate ? Number(editFormData.taxRate) : undefined,
            phoneNumber: editFormData.phoneNumber || undefined,
            description: editFormData.description || undefined,
          },
        },
      });
      toast.success('Location updated successfully');
      handleCloseEdit();
      await refetch();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update location');
    }
  };

  useEffect(() => {
    const checkGoogleMapsLoaded = () => {
      if (typeof window !== 'undefined' && window.google?.maps?.places) {
        setIsGoogleMapsLoaded(true);
        return;
      }
      setTimeout(checkGoogleMapsLoaded, 100);
    };
    checkGoogleMapsLoaded();
  }, []);

  const handlePlaceSelection = (place: any) => {
    if (!place?.geometry?.location) {
      toast.error('No details available for the selected place');
      return;
    }

    let cityName = '';
    let sublocalityName = '';
    let postalTownName = '';
    let adminLevel2Name = '';
    let stateName = '';
    let zipCodeValue = '';
    let country = '';

    place.address_components?.forEach((component: any) => {
      const types = component.types;
      if (types.includes('locality')) {
        cityName = component.long_name;
      }
      if (types.includes('postal_town')) {
        postalTownName = component.long_name;
      }
      if (types.includes('sublocality_level_1') || types.includes('sublocality')) {
        sublocalityName = component.long_name;
      }
      if (types.includes('administrative_area_level_2')) {
        adminLevel2Name = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        stateName = component.short_name;
      }
      if (types.includes('postal_code')) {
        zipCodeValue = component.long_name;
      }
      if (types.includes('country')) {
        country = component.short_name;
      }
    });

    const fullAddress = place.formatted_address || '';
    let resolvedCity = cityName || sublocalityName || postalTownName || adminLevel2Name;
    if (fullAddress) {
      const addressParts = fullAddress.split(',').map((part: string) => part.trim());
      const addressCity = addressParts.length >= 2 ? addressParts[1] : '';
      if (addressCity && addressCity !== stateName && addressCity !== country) {
        resolvedCity = addressCity;
      }
    }

    const location = place.geometry.location;
    const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
    const lng = typeof location.lng === 'function' ? location.lng() : location.lng;

    setFormData(prev => ({
      ...prev,
      name: prev.name || place.name || '',
      address: fullAddress,
      city: resolvedCity,
      state: stateName,
      zipCode: zipCodeValue,
      latitude: String(lat),
      longitude: String(lng),
    }));
  };

  useEffect(() => {
    if (!isGoogleMapsLoaded || !autocompleteInputRef.current) {
      return;
    }

    if (autocompleteRef.current && window.google?.maps?.event) {
      window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      autocompleteRef.current = null;
    }

    const initializeAutocomplete = (retryCount = 0) => {
      if (!window.google?.maps?.places?.Autocomplete) {
        if (retryCount < 10) {
          setTimeout(() => initializeAutocomplete(retryCount + 1), 100);
        }
        return;
      }

      if (!autocompleteInputRef.current) {
        return;
      }

      try {
        const autocomplete = new window.google.maps.places.Autocomplete(
          autocompleteInputRef.current,
          {
            fields: ['formatted_address', 'address_components', 'geometry', 'name'],
          },
        );

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          handlePlaceSelection(place);
        });

        autocompleteRef.current = autocomplete;
      } catch (error) {
        if (retryCount >= 10) {
          toast.error('Failed to initialize address autocomplete. Please check your API key.');
        } else {
          setTimeout(() => initializeAutocomplete(retryCount + 1), 100);
        }
      }
    };

    const timeoutId = setTimeout(() => {
      initializeAutocomplete();
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [isGoogleMapsLoaded]);

  // Initialize autocomplete for edit modal
  useEffect(() => {
    if (!isGoogleMapsLoaded || !editingLocation || !editAutocompleteInputRef.current) {
      return;
    }

    if (editAutocompleteRef.current && window.google?.maps?.event) {
      window.google.maps.event.clearInstanceListeners(editAutocompleteRef.current);
      editAutocompleteRef.current = null;
    }

    const initializeEditAutocomplete = (retryCount = 0) => {
      if (!window.google?.maps?.places?.Autocomplete) {
        if (retryCount < 10) {
          setTimeout(() => initializeEditAutocomplete(retryCount + 1), 100);
        }
        return;
      }

      if (!editAutocompleteInputRef.current) {
        return;
      }

      try {
        const autocomplete = new window.google.maps.places.Autocomplete(
          editAutocompleteInputRef.current,
          {
            fields: ['formatted_address', 'address_components', 'geometry', 'name'],
          },
        );

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          handleEditPlaceSelection(place);
        });

        editAutocompleteRef.current = autocomplete;
      } catch (error) {
        if (retryCount >= 10) {
          toast.error('Failed to initialize address autocomplete. Please check your API key.');
        } else {
          setTimeout(() => initializeEditAutocomplete(retryCount + 1), 100);
        }
      }
    };

    const timeoutId = setTimeout(() => {
      initializeEditAutocomplete();
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      if (editAutocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(editAutocompleteRef.current);
        editAutocompleteRef.current = null;
      }
    };
  }, [isGoogleMapsLoaded, editingLocation]);

  const handleSubmit = async () => {
    try {
      const persistedAddress = streetAddressForLocationSave(
        formData.address,
        formData.city,
        formData.state,
        formData.zipCode,
      );
      await createPublicLocation({
        variables: {
          input: {
            name: formData.name,
            address: persistedAddress,
            city: formData.city,
            state: formData.state,
            zipCode: formData.zipCode,
            latitude: Number(formData.latitude),
            longitude: Number(formData.longitude),
            taxRate: formData.taxRate ? Number(formData.taxRate) : undefined,
            phoneNumber: formData.phoneNumber || undefined,
            description: formData.description || undefined,
            locationType: LocationType.Airport,
            airportCode: formData.airportCode.trim().toUpperCase(),
          },
        },
      });
      toast.success('Airport created and enabled for this brand');
      setFormData({
        airportCode: '',
        name: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        latitude: '',
        longitude: '',
        taxRate: '',
        phoneNumber: '',
        description: '',
      });
      await refetch();
    } catch (error: any) {
      toast.error(error.message || 'Failed to create public location');
    }
  };

  const publicLocations: PublicLocation[] = data?.publicLocations || [];

  return (
    <PageContainer width="xl" canvas>
      <PageHeader
        eyebrow="Admin"
        title="Airports"
        description="Public airport pickup locations are shared by every brand. Turn an existing airport on for this brand with the switch; only create one that does not exist yet."
      />

      <Stack>
        {!deliveryOffered && (
          <Alert tone="info" title="Delivery is off" data-testid="delivery-off-notice">
            Delivery is off on this site. Your airport choices are kept and apply when you offer
            delivery again. <SettingsLink to="/admin/brand-settings">Car settings</SettingsLink>
          </Alert>
        )}
        <Card>
          <CardHeader
            title="Create public airport"
            description="Check the list below first: an airport another brand added is turned on for your brand with the switch, not created again. A new airport needs a unique three-letter code and must be more than 2 miles from every existing airport."
          />
          <CardBody>
            <FormGrid>
              <Field label="Airport code (3 letters)">
                <Input
                  value={formData.airportCode}
                  maxLength={3}
                  placeholder="SFO"
                  onChange={e => handleChange('airportCode', e.target.value.toUpperCase())}
                />
              </Field>
              <Field label="Name">
                <Input value={formData.name} onChange={e => handleChange('name', e.target.value)} />
              </Field>
              <Field label="Address">
                <Input
                  ref={autocompleteInputRef}
                  value={formData.address}
                  onChange={e => handleChange('address', e.target.value)}
                  placeholder={isGoogleMapsLoaded ? 'Start typing an address...' : 'Loading...'}
                  disabled={!isGoogleMapsLoaded}
                />
              </Field>
              <Field label="City">
                <Input value={formData.city} onChange={e => handleChange('city', e.target.value)} />
              </Field>
              <Field label="State">
                <Input
                  value={formData.state}
                  onChange={e => handleChange('state', e.target.value)}
                />
              </Field>
              <Field label="Zip Code">
                <Input
                  value={formData.zipCode}
                  onChange={e => handleChange('zipCode', e.target.value)}
                />
              </Field>
              <Field label="Latitude">
                <Input
                  type="number"
                  value={formData.latitude}
                  onChange={e => handleChange('latitude', e.target.value)}
                />
              </Field>
              <Field label="Longitude">
                <Input
                  type="number"
                  value={formData.longitude}
                  onChange={e => handleChange('longitude', e.target.value)}
                />
              </Field>
              <Field label="Tax Rate (%)">
                <Input
                  type="number"
                  value={formData.taxRate}
                  onChange={e => handleChange('taxRate', e.target.value)}
                />
              </Field>
              <Field label="Phone Number">
                <Input
                  value={formData.phoneNumber}
                  onChange={e => handleChange('phoneNumber', e.target.value)}
                />
              </Field>
              <Field label="Description">
                <Textarea
                  rows={3}
                  value={formData.description}
                  onChange={e => handleChange('description', e.target.value)}
                />
              </Field>
            </FormGrid>
          </CardBody>
          <CardFooter>
            <Button
              text="Create airport"
              type="button"
              size="sm"
              onClick={handleSubmit}
              isLoading={creating}
            />
          </CardFooter>
        </Card>

        <Card>
          <CardHeader
            title="Existing public airports"
            description={`${publicLocations.length} airport${publicLocations.length === 1 ? '' : 's'} on the platform.`}
          />
          {loading ? (
            <LoadingBlock label="Loading airports" />
          ) : publicLocations.length === 0 ? (
            <EmptyState title="No public airports yet" description="Create the first one above." />
          ) : (
            <CardBody $padding="none">
              <TableWrap>
                <Table>
                  <THead>
                    <tr>
                      <TH>Code</TH>
                      <TH>Name</TH>
                      <TH>Address</TH>
                      <TH>Status</TH>
                      <TH>On this brand</TH>
                      <TH $align="right">Actions</TH>
                    </tr>
                  </THead>
                  <TBody>
                    {publicLocations.map(location => (
                      <TR
                        key={location.id}
                        style={location.isActive === false ? { opacity: 0.6 } : undefined}
                      >
                        <TD $nowrap>
                          <Badge tone={location.airportCode ? 'brand' : 'neutral'}>
                            {location.airportCode ?? '—'}
                          </Badge>
                        </TD>
                        <TD>
                          <Strong>{location.name}</Strong>
                        </TD>
                        <TD $muted>
                          {location.address}
                          <Muted $size="xs" style={{ display: 'block' }}>
                            {location.city}, {location.state}
                          </Muted>
                        </TD>
                        <TD $nowrap>
                          <Badge tone={location.isActive === false ? 'error' : 'success'} dot>
                            {location.isActive === false ? 'Off' : 'On'}
                          </Badge>
                        </TD>
                        <TD $nowrap>
                          <ToggleLabel>
                            <SwitchComponent
                              isActive={Boolean(location.enabledOnBrand)}
                              inputTestId={`brand-airport-${location.id}`}
                              onChange={async e => {
                                if (togglingBrand || location.isActive === false) return;
                                try {
                                  await setBrandAirportEnabled({
                                    variables: {
                                      locationId: location.id,
                                      enabled: e.target.checked,
                                    },
                                  });
                                  await refetch();
                                } catch (err: any) {
                                  toast.error(err?.message || 'Failed to update airport');
                                }
                              }}
                            />
                            <span>{location.enabledOnBrand ? 'Enabled' : 'Not enabled'}</span>
                          </ToggleLabel>
                        </TD>
                        <TDActions>
                          {isPlatformOwner ? (
                            <InlineRow>
                              <Button
                                text="Edit"
                                theme="secondary"
                                size="sm"
                                type="button"
                                onClick={() => handleEdit(location)}
                              />
                              <Button
                                text={location.isActive === false ? 'Turn on' : 'Turn off'}
                                theme="text"
                                size="sm"
                                type="button"
                                isDisabled={togglingActive}
                                onClick={async () => {
                                  const turningOff = location.isActive !== false;
                                  if (
                                    turningOff &&
                                    !window.confirm(
                                      `Turn off ${location.name}? Guests can no longer pick it for new bookings on any brand. Booked trips go ahead. Each brand that enabled it and its hosts who deliver there will be emailed.`,
                                    )
                                  ) {
                                    return;
                                  }
                                  try {
                                    await setAirportActive({
                                      variables: { locationId: location.id, isActive: !turningOff },
                                    });
                                    await refetch();
                                  } catch (err: any) {
                                    toast.error(err?.message || 'Failed to update airport');
                                  }
                                }}
                              />
                            </InlineRow>
                          ) : (
                            <Muted $size="xs">Platform owner edits airports</Muted>
                          )}
                        </TDActions>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableWrap>
            </CardBody>
          )}
        </Card>
      </Stack>

      <Modal
        isOpen={!!editingLocation}
        onClose={handleCloseEdit}
        title="Edit location"
        modalWidth="720px"
      >
        {editingLocation && (
          <>
            <ModalTitle>Edit location</ModalTitle>
            <FormGrid>
              <Field label="Name">
                <Input
                  value={editFormData.name}
                  onChange={e => handleEditChange('name', e.target.value)}
                />
              </Field>
              <Field label="Address">
                <Input
                  ref={editAutocompleteInputRef}
                  value={editFormData.address}
                  onChange={e => handleEditChange('address', e.target.value)}
                  placeholder={isGoogleMapsLoaded ? 'Start typing an address...' : 'Loading...'}
                  disabled={!isGoogleMapsLoaded}
                />
              </Field>
              <Field label="City">
                <Input
                  value={editFormData.city}
                  onChange={e => handleEditChange('city', e.target.value)}
                />
              </Field>
              <Field label="State">
                <Input
                  value={editFormData.state}
                  onChange={e => handleEditChange('state', e.target.value)}
                />
              </Field>
              <Field label="Zip Code">
                <Input
                  value={editFormData.zipCode}
                  onChange={e => handleEditChange('zipCode', e.target.value)}
                />
              </Field>
              <Field label="Latitude">
                <Input
                  type="number"
                  value={editFormData.latitude}
                  onChange={e => handleEditChange('latitude', e.target.value)}
                />
              </Field>
              <Field label="Longitude">
                <Input
                  type="number"
                  value={editFormData.longitude}
                  onChange={e => handleEditChange('longitude', e.target.value)}
                />
              </Field>
              <Field label="Tax Rate (%)">
                <Input
                  type="number"
                  value={editFormData.taxRate}
                  onChange={e => handleEditChange('taxRate', e.target.value)}
                />
              </Field>
              <Field label="Phone Number">
                <Input
                  value={editFormData.phoneNumber}
                  onChange={e => handleEditChange('phoneNumber', e.target.value)}
                />
              </Field>
              <Field label="Description">
                <Textarea
                  rows={3}
                  value={editFormData.description}
                  onChange={e => handleEditChange('description', e.target.value)}
                />
              </Field>
            </FormGrid>
            <ModalActions>
              <Button text="Cancel" theme="secondary" type="button" onClick={handleCloseEdit} />
              <Button
                text="Save changes"
                type="button"
                onClick={handleSaveEdit}
                isLoading={updating}
              />
            </ModalActions>
          </>
        )}
      </Modal>
    </PageContainer>
  );
};

export default AdminLocationsPage;
